/* eslint-disable no-barrel-files/no-barrel-files -- Compatibility layer that re-exports UIKit Typography sub-components */

import { Typography as CompassTypography } from "@circleco/compass/components/Typography";
import { allowedClassesFor } from "@circleco/compass/helpers/allowedClassesFor";

import { BodyLg as UIKitBodyLg } from "@/react/components/shared/uikit/Typography/components/Body/BodyLg";
import type { BodyLgProps } from "@/react/components/shared/uikit/Typography/components/Body/BodyLg";
import { BodyMd as UIKitBodyMd } from "@/react/components/shared/uikit/Typography/components/Body/BodyMd";
import type { BodyMdProps } from "@/react/components/shared/uikit/Typography/components/Body/BodyMd";
import { BodySm as UIKitBodySm } from "@/react/components/shared/uikit/Typography/components/Body/BodySm";
import type { BodySmProps } from "@/react/components/shared/uikit/Typography/components/Body/BodySm";
import { BodyXs as UIKitBodyXs } from "@/react/components/shared/uikit/Typography/components/Body/BodyXs";
import type { BodyXsProps } from "@/react/components/shared/uikit/Typography/components/Body/BodyXs";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { mapColor, extractExtraClasses } from "./colorMap";
import { useTypographyFlag, filterClassName, stripUIKitProps } from "./shared";
import type { TypographyElement } from "./shared";

export type { BodyLgProps, BodyMdProps, BodySmProps, BodyXsProps };

export const BodyLg = ({
  children,
  color,
  truncate,
  leading,
  as = "p",
  "data-testid": dataTestId,
  ...rest
}: BodyLgProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitBodyLg
        color={color}
        truncate={truncate}
        leading={leading}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitBodyLg>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="body-md"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "p"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        extractExtraClasses(color) ?? "",
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};

export const BodyMd = ({
  children,
  color,
  truncate,
  leading,
  textAlign,
  as = "p",
  "data-testid": dataTestId,
  ...rest
}: BodyMdProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitBodyMd
        color={color}
        truncate={truncate}
        leading={leading}
        textAlign={textAlign}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitBodyMd>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="body-md"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "p"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        classNames(filterClassName(textAlign), extractExtraClasses(color)),
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};

export const BodySm = ({
  children,
  color,
  truncate,
  leading,
  textTransform,
  as = "p",
  "data-testid": dataTestId,
  ...rest
}: BodySmProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitBodySm
        color={color}
        truncate={truncate}
        leading={leading}
        textTransform={textTransform}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitBodySm>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="body-sm"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "p"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        classNames(filterClassName(textTransform), extractExtraClasses(color)),
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};

export const BodyXs = ({
  children,
  color,
  truncate,
  leading,
  as = "p",
  "data-testid": dataTestId,
  ...rest
}: BodyXsProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitBodyXs
        color={color}
        truncate={truncate}
        leading={leading}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitBodyXs>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="caption"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "p"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        extractExtraClasses(color) ?? "",
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};
